import { useMemo, useRef, useState } from "react";
import type { Asesor } from "@/lib/api";

type Props = {
  asesores: Asesor[];
  /** ID del asesor asignado hoy (null = ninguno). Solo para resaltarlo. */
  value: number | null;
  onSelect: (a: Asesor) => void;
  /** Si viene y hay un asesor asignado, se muestra la fila "Quitar asesor". */
  onClear?: () => void;
  /** Se llama al presionar Escape en el campo de búsqueda. */
  onEscape?: () => void;
  autoFocus?: boolean;
  cargando?: boolean;
  /** Deshabilita toda la lista mientras se guarda. */
  ocupado?: boolean;
  /** Alto máximo de la lista (clases Tailwind). */
  altoLista?: string;
  /**
   * Si true, la lista se dibuja flotando (absolute) debajo del buscador en
   * vez de empujar el contenido. Para dropdowns; en un modal va en flujo.
   * Quien la use en modo flotante debe darle un contenedor `relative`.
   */
  flotante?: boolean;
};

/**
 * Buscador + lista de asesores comerciales, **sin caja ni posicionamiento**:
 * quien la usa decide si la mete en un dropdown flotante (`AsesorPicker`) o
 * plana dentro de un modal (`AsignarAsesorModal`). Así las dos entradas a
 * "cambiar asesor" se ven y se comportan igual.
 *
 * No hace IO: recibe la lista ya cargada (ver `useAsesores`) y avisa hacia
 * arriba qué eligió el usuario.
 */
export default function ListaAsesores({
  asesores,
  value,
  onSelect,
  onClear,
  onEscape,
  autoFocus = false,
  cargando = false,
  ocupado = false,
  altoLista = "max-h-72",
  flotante = false,
}: Props) {
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const seleccionado = useMemo(
    () => asesores.find((a) => a.id === value) ?? null,
    [asesores, value],
  );

  const filtrados = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return asesores.slice(0, 60);
    return asesores
      .filter(
        (a) =>
          a.nombre.toLowerCase().includes(q) ||
          (a.email ?? "").toLowerCase().includes(q),
      )
      .slice(0, 60);
  }, [asesores, query]);

  return (
    <div>
      <input
        ref={inputRef}
        type="text"
        className="input w-full text-sm"
        placeholder="Buscar asesor…"
        aria-label="Buscar asesor comercial"
        autoFocus={autoFocus}
        disabled={ocupado}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            onEscape?.();
          } else if (e.key === "Enter" && filtrados.length > 0) {
            e.preventDefault();
            onSelect(filtrados[0]);
          }
        }}
      />

      <div
        className={`mt-1 rounded-md border border-ink-700 bg-ink-900 shadow-2xl overflow-hidden ${
          flotante ? "absolute z-30 w-full" : ""
        }`}
      >
        <div className={`overflow-y-auto ${altoLista}`}>
        {filtrados.length === 0 ? (
          <p className="px-3 py-2 text-sm text-zinc-500">
            {cargando
              ? "Cargando asesores…"
              : query.trim()
                ? `Sin resultados para "${query}".`
                : "No hay asesores disponibles."}
          </p>
        ) : (
          <ul>
            {filtrados.map((a) => (
              <li key={a.id} className="border-b border-ink-800 last:border-b-0">
                <button
                  type="button"
                  disabled={ocupado}
                  // Se elige en mousedown para ganarle al listener global que
                  // cierra el dropdown (y al blur del campo de búsqueda), que
                  // si no se comen el click. El keydown cubre el teclado: sin
                  // onClick no hay riesgo de disparar dos veces.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onSelect(a);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onSelect(a);
                    }
                  }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-ink-800 transition disabled:opacity-50 ${
                    seleccionado?.id === a.id
                      ? "bg-ink-800/70 text-accent-400"
                      : "text-zinc-200"
                  }`}
                >
                  <span className="truncate block">
                    {a.nombre}
                    {seleccionado?.id === a.id && (
                      <span className="text-[10px] text-zinc-500"> · actual</span>
                    )}
                  </span>
                  {a.email && (
                    <span className="text-xs text-zinc-500 truncate block">
                      {a.email}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
        </div>

        {/* "Quitar asesor" — fuera del área con scroll para que no se pierda
            abajo cuando la lista es larga */}
        {onClear && seleccionado && (
          <div className="border-t border-ink-800">
            <button
              type="button"
              disabled={ocupado}
              onMouseDown={(e) => {
                e.preventDefault();
                onClear();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onClear();
                }
              }}
              className="w-full text-left px-3 py-2 text-sm text-zinc-400 hover:bg-ink-800 hover:text-zinc-200 transition flex items-center gap-2 disabled:opacity-50"
            >
              <span className="text-zinc-500">✕</span>
              <span>Quitar asesor</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
